import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { observer } from "mobx-react";
import styled, { keyframes } from "styled-components";

import { ShareAltOutlined } from "@ant-design/icons";

import { BasicImgWrapper, BasicText, COLORS, FontFamilyTypeEnum, Z_INDEX } from "../styled";
import { AppServiceContext, AppServiceInstance } from "../../services/app";
import { Routes } from "../../utils/routes";
import { teamPolicyLinks } from "../../utils/teamPolicy";
import { StorageServiceContext, StorageServiceInstance } from "../../services/storage";
import { cognitoSignOut } from "../../services/cognito-auth";
import { clearBearerToken } from "../../axios/instance";
import { MomentServiceContext, MomentServiceInstance } from "../../services/moment";
import { getCustomerProfileRequest } from "../../axios/routes/user";
import { getShareUrl } from "../MomentoDownloadButton";
import { CustomerProfileSummary } from "../../shared/interfaces";
import { trackFanEngagement, trackFanSocialShare } from "../../utils/clientAnalytics";
import { isEuTeamRegion } from "../../utils/teamRegion";
import { rememberMomentoSignOutStamp } from "../../utils/clearMomentoSiteData";

const CARD_SHADOW = "0 4px 20px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)";
const NAVY = "#1A2C42";
const BORDER_LIGHT = "#E5E7EB";
const TEXT_MUTED = "#6B7280";

const FadeInAnimation = (maxOpacity: number) => keyframes`
  0% { transform: translateX(100%); opacity: 0; }
  1% { transform: translateX(0); opacity: 0; }
  100% { transform: translateX(0); opacity: ${maxOpacity}; }
`;

const FadeOutAnimation = (maxOpacity: number) => keyframes`
  0% { transform: translateX(0); opacity: ${maxOpacity}; }
  99% { transform: translateX(0); opacity: 0; }
  100% { transform: translateX(100%); opacity: 0; }
`;

const SideNavigationOverlay = styled.div<{ opened: boolean; initiated: boolean }>`
  width: 100%;
  height: 100%;
  background: ${COLORS.BLACK};
  position: fixed;
  z-index: ${Z_INDEX.HIGHEST + 1};
  opacity: ${({ opened }) => (opened ? "0.5" : "0")};
  transform: translate(${({ opened }) => (opened ? "0" : "9999px")});
  animation: ${({ opened }) => (opened ? FadeInAnimation(0.5) : FadeOutAnimation(0.5))};
  animation-duration: 0.3s;
  transition: opacity 0.2s ease;
`;

const SideNavigationWrapper = styled.div<{ opened: boolean; initiated: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 85%;
  max-width: 340px;
  background: ${COLORS.WHITE};
  box-shadow: ${CARD_SHADOW};
  padding: 0;
  z-index: ${Z_INDEX.HIGHEST + 1};
  position: fixed;
  right: 0;
  top: 0;
  transform: translateX(${({ opened }) => (opened ? "0" : "100%")});
  animation: ${({ opened, initiated }) => (!initiated ? "none" : opened ? FadeInAnimation(1) : FadeOutAnimation(1))};
  animation-duration: 0.35s;
  overflow: hidden;
`;

const MenuScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  ::-webkit-scrollbar { display: none; }
`;

const BottomSection = styled.div`
  flex-shrink: 0;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid ${BORDER_LIGHT};
  background: linear-gradient(180deg, #fafbfc 0%, ${COLORS.WHITE} 100%);
`;

const NavAvatarWrapper = styled.div<{ $accentColor?: string }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid ${({ $accentColor }) => $accentColor || COLORS.BLUE_MAIN};
  overflow: hidden;
  background: ${COLORS.GREY_MAIN};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const NavAvatarInitials = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.GREY_TEXT};
`;

const NavAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NavUserName = styled.div`
  font-family: ${FontFamilyTypeEnum.MONTSERRAT};
  font-size: 16px;
  font-weight: 600;
  color: ${NAVY};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SideNavigationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px 16px;
  border-bottom: 1px solid ${BORDER_LIGHT};
  background: linear-gradient(180deg, #fafbfc 0%, ${COLORS.WHITE} 100%);
`;

const SideNavigationHeaderTitle = styled.div`
  font-family: ${FontFamilyTypeEnum.MONTSERRAT};
  font-size: 13px;
  font-weight: 600;
  color: ${NAVY};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const SideNavigationHeaderRight = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px;
  &:hover { opacity: 0.7; }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  font-weight: 300;
  color: #374151;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { color: #1f2937; }
`;

const SideNavigationRow = styled.div`
  min-height: 52px;
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${BORDER_LIGHT};
  transition: background 0.15s ease;
  &:last-of-type { border-bottom: none; }
  &:active { background: #f9fafb; }
`;

const SideNavigationRowContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const SideNavigationRowText = styled(BasicText)<{ disabled?: boolean }>`
  font-family: ${FontFamilyTypeEnum.MONTSERRAT} !important;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
  color: ${({ disabled }) => (disabled ? TEXT_MUTED : NAVY)};
  opacity: ${({ disabled }) => (disabled ? "0.6" : "1")};
`;

const SideNavigationRowState = styled.span`
  font-family: ${FontFamilyTypeEnum.MONTSERRAT};
  font-size: 12px;
  font-weight: 500;
  color: ${COLORS.GREEN_DARK};
  letter-spacing: 0.02em;
`;

const SideNavigationChevron = styled.span`
  font-size: 12px;
  color: ${TEXT_MUTED};
  margin-left: 8px;
  flex-shrink: 0;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  width: 100%;
  display: block;
  &:focus, &:hover, &:visited, &:link, &:active { text-decoration: none; }
  &:hover ${SideNavigationRow} { background: #f9fafb; }
`;

const MenuItems = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  flex: 1;
`;

const MenuSection = styled.div`
  padding: 12px 0 4px;
  &:first-of-type { padding-top: 0; }
`;

const MenuSectionLabel = styled.div`
  font-family: ${FontFamilyTypeEnum.MONTSERRAT};
  font-size: 10px;
  font-weight: 600;
  color: ${TEXT_MUTED};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0 20px 8px;
`;

const SignOutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 52px;
  padding: 14px 20px;
  margin: 0;
  border: none;
  border-top: 1px solid ${BORDER_LIGHT};
  background: ${COLORS.WHITE};
  font-family: ${FontFamilyTypeEnum.MONTSERRAT};
  font-size: 15px;
  font-weight: 500;
  color: #DC2626;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: #FEF2F2; }
  &:active { background: #FEE2E2; }
`;

const BottomImage = styled.div`
  display: flex;
  height: 72px;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: #000000;
  img {
    max-width: 180px;
    max-height: 48px;
    width: auto;
    height: auto;
    object-fit: contain;
  }
`;

export const NavigationComponent = observer((): JSX.Element => {
  const { pathname } = useLocation();

  const { navigationOpened } = useContext(AppServiceContext);

  const [initiated, setInitiated] = useState(false);

  const {
    selectedRow, selectedSeat, selectedSection, selectedGameData, selectedMoment,
    selectedFirstName, selectedLastName, selectedEmail, selectedPhone, selectedCustomerId,
    selectedSectionIsSpecial, selectedSpecialSection, selectedSuite, showSuites,
    selectedVerifyLastName, team1Color, isReturningFanAuthenticated, welcomeTeamContext,
  } = useContext(StorageServiceContext);
  const [userProfile, setUserProfile] = useState<CustomerProfileSummary | null>(null);

  const getInitials = (firstName: string, lastName: string): string => {
    const first = (firstName || "").charAt(0).toUpperCase();
    const last = (lastName || "").charAt(0).toUpperCase();
    return first && last ? `${first}${last}` : first || last || "?";
  };

  const displayName = `${selectedFirstName || ""} ${selectedLastName || ""}`.trim() || "Guest";
  const accentColor = team1Color || COLORS.BLUE_MAIN;
  /** Fan-facing profile UI; data collection / operator still use profiles when false */
  const profileEnabled = selectedGameData?.team1?.profileEnabled === true;
  const { momentList } = useContext(MomentServiceContext);

  const usersPlace = useMemo(() => {
    if (selectedSectionIsSpecial) {
      if (selectedSpecialSection) {
        const specialSectionDetails = `Sec. ${selectedSpecialSection}`.substring(0, 17);
        return `(${specialSectionDetails})`;
      }
      return null;
    }

    if (showSuites && selectedSuite) {
      return `Suite ${selectedSuite}`;
    }
    if (selectedSection && selectedRow && selectedSeat) {
      return `(Sec.${selectedSection} Row.${selectedRow} Seat.${selectedSeat})`;
    }
    return null;
  }, [
    selectedSection, selectedRow, selectedSeat, selectedSectionIsSpecial, selectedSpecialSection,
    selectedSuite, showSuites,
  ]);

  const needVerifySeat = useMemo(() => {
    return StorageServiceInstance.shouldShowSurnameVerificationStep();
  }, [selectedGameData, selectedVerifyLastName]);

  const userDetails = useMemo(() => {
    if (needVerifySeat) {
      if (selectedVerifyLastName) {
        return selectedVerifyLastName;
      }

      return null;
    }

    if (selectedFirstName && selectedLastName && selectedEmail && selectedPhone) {
      const details = `${selectedFirstName} ${selectedLastName}`.substring(0, 17);
      return `(${details})`;
    }
    return null;
  }, [
    selectedFirstName, selectedLastName,
    selectedEmail, selectedPhone,
    needVerifySeat, selectedVerifyLastName,
  ]);

  const gameData = useMemo(() => {
    if (!selectedGameData || !selectedGameData.team1 || !selectedGameData.team2) {
      return null;
    }

    const { team1, team2 } = selectedGameData;
    const shortName = (name?: string) => {
      const label = name || "Team";
      return label.length <= 8 ? label : `${label.substring(0, 8)}.`;
    };
    const firstTeamName = shortName(team1.name);
    const secondTeamName = shortName(team2.name);

    return `(${firstTeamName} vs. ${secondTeamName})`;
  }, [selectedGameData]);

  useEffect(() => {
    if (navigationOpened) {
      setInitiated(true);
    }
  }, [navigationOpened]);

  useEffect(() => {
    setInitiated(false);
  }, [pathname]);

  useEffect(() => {
    if (!navigationOpened || !selectedCustomerId || !profileEnabled) {
      setUserProfile(null);
      return undefined;
    }
    let cancelled = false;
    getCustomerProfileRequest(selectedCustomerId)
      .then((profile) => {
        if (!cancelled) setUserProfile(profile);
      })
      .catch(() => {
        if (!cancelled) setUserProfile(null);
      });
    return () => { cancelled = true; };
  }, [navigationOpened, selectedCustomerId, profileEnabled]);

  const onFeedbackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    AppServiceInstance.openSupportFeedback("nav_feedback");
    AppServiceInstance.closeNavigationBar();
  };

  const onHelpClick = (e: React.MouseEvent) => {
    e.preventDefault();
    AppServiceInstance.openSupportFeedback("nav_help");
    AppServiceInstance.closeNavigationBar();
  };

  const onShareWithFriends = async (e: React.MouseEvent) => {
    e.preventDefault();
    const hostname = selectedGameData?.team1?.hostname;
    const teamName = selectedGameData?.team1?.name || "Momento";
    const shareUrl = getShareUrl(hostname);
    const shareText = `Check out my ${teamName} experience on Momento!`;

    if (navigator.share) {
      try {
        trackFanSocialShare("native", {
          ui_surface: "nav_share_with_friends",
          share_method: "navigator_share",
        });
        await navigator.share({ title: teamName, text: shareText, url: shareUrl });
      } catch {
        // user cancelled or share failed silently
      }
    } else {
      trackFanSocialShare("native", {
        ui_surface: "nav_share_with_friends",
        share_method: "clipboard_or_link",
      });
      try {
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        // eslint-disable-next-line no-alert
        alert("Link copied to clipboard!");
      } catch {
        window.open(shareUrl, "_blank");
      }
    }
    AppServiceInstance.closeNavigationBar();
  };

  const menuTeam = selectedGameData?.team1 || welcomeTeamContext;
  const isEuMenu = isEuTeamRegion(menuTeam?.teamRegion);
  const showMenuBarOptOut = menuTeam?.menuBarOptOutEnabled === true;
  const optOutHref = useMemo(() => {
    const params = new URLSearchParams();
    if (menuTeam?._id) params.set("teamId", String(menuTeam._id));
    const teamName = (menuTeam?.name || "").replace(/\s*\(.*?\)\s*/g, "").trim();
    if (teamName) params.set("team", teamName);
    const qs = params.toString();
    return qs ? `${Routes.OPT_OUT}?${qs}` : Routes.OPT_OUT;
  }, [menuTeam?._id, menuTeam?.name]);

  const onSignOut = async () => {
    cognitoSignOut();
    clearBearerToken();
    await StorageServiceInstance.resetAllStateForSignOut();
    MomentServiceInstance.resetAllStateForSignOut();
    rememberMomentoSignOutStamp(String(Date.now()));
    AppServiceInstance.closeNavigationBar();
    window.location.replace(`${window.location.origin}/`);
  };

  if (!initiated) {
    return null;
  }

  return (
    <>
      <SideNavigationOverlay
        initiated={initiated}
        opened={navigationOpened}
        onClick={AppServiceInstance.toggleNavigationBar}
      />
      <SideNavigationWrapper opened={navigationOpened} initiated={initiated}>
        <MenuScrollArea>
          <MenuItems>
            {profileEnabled && (
              <UserSection>
                <NavAvatarWrapper $accentColor={accentColor}>
                  {userProfile?.avatarUrl ? (
                    <NavAvatarImage src={userProfile.avatarUrl} alt={displayName} />
                  ) : (
                    <NavAvatarInitials>
                      {getInitials(selectedFirstName, selectedLastName)}
                    </NavAvatarInitials>
                  )}
                </NavAvatarWrapper>
                <NavUserName>{displayName}</NavUserName>
              </UserSection>
            )}

            <SideNavigationHeader>
              <SideNavigationHeaderTitle>Menu</SideNavigationHeaderTitle>
              <SideNavigationHeaderRight>
                <CloseButton type="button" onClick={AppServiceInstance.toggleNavigationBar}>×</CloseButton>
              </SideNavigationHeaderRight>
            </SideNavigationHeader>

            <MenuSection>
              <MenuSectionLabel>Your journey</MenuSectionLabel>
              <StyledLink to={Routes.GAMES}>
                <SideNavigationRow>
                  <SideNavigationRowContent>
                    <SideNavigationRowText>Select Game</SideNavigationRowText>
                    {gameData && <SideNavigationRowState>{gameData}</SideNavigationRowState>}
                  </SideNavigationRowContent>
                  <SideNavigationChevron>›</SideNavigationChevron>
                </SideNavigationRow>
              </StyledLink>

              <StyledLink to={Routes.SEAT} onClick={(e) => !gameData && e.preventDefault()}>
                <SideNavigationRow>
                  <SideNavigationRowContent>
                    <SideNavigationRowText disabled={!gameData}>Select Seat</SideNavigationRowText>
                    {usersPlace && <SideNavigationRowState>{usersPlace}</SideNavigationRowState>}
                  </SideNavigationRowContent>
                  <SideNavigationChevron>›</SideNavigationChevron>
                </SideNavigationRow>
              </StyledLink>

              {!isReturningFanAuthenticated && !selectedCustomerId && (
                <StyledLink
                  to={needVerifySeat ? Routes.VERIFY_SEAT : Routes.DETAILS}
                  onClick={(e) => (!usersPlace) && e.preventDefault()}
                >
                  <SideNavigationRow>
                    <SideNavigationRowContent>
                      <SideNavigationRowText disabled={!usersPlace || !momentList.length}>
                        Contact Info
                      </SideNavigationRowText>
                      {userDetails && <SideNavigationRowState>{userDetails}</SideNavigationRowState>}
                    </SideNavigationRowContent>
                    <SideNavigationChevron>›</SideNavigationChevron>
                  </SideNavigationRow>
                </StyledLink>
              )}

              <StyledLink
                to={Routes.MY_MOMENTS}
                onClick={(e) => (!usersPlace || !userDetails || !momentList.length)
                  && e.preventDefault()}
              >
                <SideNavigationRow>
                  <SideNavigationRowContent>
                    <SideNavigationRowText disabled={!usersPlace || !userDetails || !momentList.length}>
                      {isEuMenu ? "My Photos" : "My Momentos"}
                    </SideNavigationRowText>
                  </SideNavigationRowContent>
                  <SideNavigationChevron>›</SideNavigationChevron>
                </SideNavigationRow>
              </StyledLink>

              {profileEnabled && (
                <StyledLink
                  to={Routes.PROFILE}
                  onClick={() => trackFanEngagement("fan_profile_nav", { ui_surface: "side_nav" })}
                >
                  <SideNavigationRow>
                    <SideNavigationRowContent>
                      <SideNavigationRowText>
                        My Profile
                      </SideNavigationRowText>
                    </SideNavigationRowContent>
                    <SideNavigationChevron>›</SideNavigationChevron>
                  </SideNavigationRow>
                </StyledLink>
              )}
            </MenuSection>

            <MenuSection>
              <MenuSectionLabel>Share</MenuSectionLabel>
              <StyledLink to="#" onClick={onShareWithFriends}>
                <SideNavigationRow>
                  <SideNavigationRowContent>
                    <SideNavigationRowText>
                      <ShareAltOutlined style={{ marginRight: 8 }} />
                      Share with Friends
                    </SideNavigationRowText>
                  </SideNavigationRowContent>
                  <SideNavigationChevron>›</SideNavigationChevron>
                </SideNavigationRow>
              </StyledLink>
            </MenuSection>

            <MenuSection>
              <MenuSectionLabel>Support</MenuSectionLabel>
              <StyledLink to="Feedback" onClick={onFeedbackClick}>
                <SideNavigationRow>
                  <SideNavigationRowContent>
                    <SideNavigationRowText>Feedback</SideNavigationRowText>
                  </SideNavigationRowContent>
                  <SideNavigationChevron>›</SideNavigationChevron>
                </SideNavigationRow>
              </StyledLink>

              <StyledLink to="Help" onClick={onHelpClick}>
                <SideNavigationRow>
                  <SideNavigationRowContent>
                    <SideNavigationRowText>Help</SideNavigationRowText>
                  </SideNavigationRowContent>
                  <SideNavigationChevron>›</SideNavigationChevron>
                </SideNavigationRow>
              </StyledLink>

              {showMenuBarOptOut && (
                <StyledLink
                  to={optOutHref}
                  onClick={() => AppServiceInstance.closeNavigationBar()}
                >
                  <SideNavigationRow>
                    <SideNavigationRowContent>
                      <SideNavigationRowText>Opt Out</SideNavigationRowText>
                    </SideNavigationRowContent>
                    <SideNavigationChevron>›</SideNavigationChevron>
                  </SideNavigationRow>
                </StyledLink>
              )}
            </MenuSection>

            <MenuSection>
              <MenuSectionLabel>Main</MenuSectionLabel>
              <StyledLink to={Routes.WELCOME}>
                <SideNavigationRow>
                  <SideNavigationRowContent>
                    <SideNavigationRowText>Back To Main</SideNavigationRowText>
                  </SideNavigationRowContent>
                  <SideNavigationChevron>›</SideNavigationChevron>
                </SideNavigationRow>
              </StyledLink>
            </MenuSection>
          </MenuItems>
        </MenuScrollArea>

        <BottomSection>
          {teamPolicyLinks(welcomeTeamContext || menuTeam).map((link) => (
            <StyledLink
              key={link.to}
              to={link.to}
              onClick={() => AppServiceInstance.closeNavigationBar()}
            >
              <SideNavigationRow>
                <SideNavigationRowContent>
                  <SideNavigationRowText>{link.label}</SideNavigationRowText>
                </SideNavigationRowContent>
                <SideNavigationChevron>›</SideNavigationChevron>
              </SideNavigationRow>
            </StyledLink>
          ))}
          <SignOutButton type="button" onClick={onSignOut}>
            Sign Out
          </SignOutButton>
          <BottomImage>
            <BasicImgWrapper src="/white-momento-logo.png" alt="Momento" cursor="default" />
          </BottomImage>
        </BottomSection>
      </SideNavigationWrapper>
    </>
  );
});
